/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Coherent Labs AD. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

const mappings = {
    FACE_BUTTON_DOWN: 0,
    FACE_BUTTON_RIGHT: 1,
    FACE_BUTTON_LEFT: 2,
    FACE_BUTTON_TOP: 3,
    LEFT_SHOULDER: 4,
    RIGHT_SHOULDER: 5,
    LEFT_SHOULDER_BOTTOM: 6,
    RIGHT_SHOULDER_BOTTOM: 7,
    SELECT: 8,
    START: 9,
    LEFT_ANALOGUE_STICK: 10,
    RIGHT_ANALOGUE_STICK: 11,
    PAD_UP: 12,
    PAD_DOWN: 13,
    PAD_LEFT: 14,
    PAD_RIGHT: 15,
    CENTER_BUTTON: 16,
    aliases: {
        'face-button-down': 'FACE_BUTTON_DOWN',
        'face-button-left': 'FACE_BUTTON_LEFT',
        'face-button-right': 'FACE_BUTTON_RIGHT',
        'face-button-top': 'FACE_BUTTON_TOP',
        'left-shoulder': 'LEFT_SHOULDER',
        'right-shoulder': 'RIGHT_SHOULDER',
        'left-shoulder-bottom': 'LEFT_SHOULDER_BOTTOM',
        'right-shoulder-bottom': 'RIGHT_SHOULDER_BOTTOM',
        select: 'SELECT',
        start: 'START',
        'left-analogue-stick': 'LEFT_ANALOGUE_STICK',
        'right-analogue-stick': 'RIGHT_ANALOGUE_STICK',
        'pad-up': 'PAD_UP',
        'pad-down': 'PAD_DOWN',
        'pad-left': 'PAD_LEFT',
        'pad-right': 'PAD_RIGHT',
        'center-button': 'CENTER_BUTTON',
        'playstation.x': 'FACE_BUTTON_DOWN',
        'playstation.square': 'FACE_BUTTON_LEFT',
        'playstation.circle': 'FACE_BUTTON_RIGHT',
        'playstation.triangle': 'FACE_BUTTON_TOP',
        'playstation.l1': 'LEFT_SHOULDER',
        'playstation.r1': 'RIGHT_SHOULDER',
        'playstation.l2': 'LEFT_SHOULDER_BOTTOM',
        'playstation.r2': 'RIGHT_SHOULDER_BOTTOM',
        'playstation.share': 'SELECT',
        'playstation.options': 'START',
        'playstation.l3': 'LEFT_ANALOGUE_STICK',
        'playstation.r3': 'RIGHT_ANALOGUE_STICK',
        'playstation.d-pad-up': 'PAD_UP',
        'playstation.d-pad-down': 'PAD_DOWN',
        'playstation.d-pad-left': 'PAD_LEFT',
        'playstation.d-pad-right': 'PAD_RIGHT',
        'playstation.center': 'CENTER_BUTTON',
        'xbox.a': 'FACE_BUTTON_DOWN',
        'xbox.x': 'FACE_BUTTON_LEFT',
        'xbox.b': 'FACE_BUTTON_RIGHT',
        'xbox.y': 'FACE_BUTTON_TOP',
        'xbox.lb': 'LEFT_SHOULDER',
        'xbox.rb': 'RIGHT_SHOULDER',
        'xbox.lt': 'LEFT_SHOULDER_BOTTOM',
        'xbox.rt': 'RIGHT_SHOULDER_BOTTOM',
        'xbox.view': 'SELECT',
        'xbox.menu': 'START',
        'xbox.left-thumbstick': 'LEFT_ANALOGUE_STICK',
        'xbox.right-thumbstick': 'RIGHT_ANALOGUE_STICK',
        'xbox.d-pad-up': 'PAD_UP',
        'xbox.d-pad-down': 'PAD_DOWN',
        'xbox.d-pad-left': 'PAD_LEFT',
        'xbox.d-pad-right': 'PAD_RIGHT',
        'xbox.center': 'CENTER_BUTTON',
    } as const,
    axisAliases: [
        'right.joystick',
        'left.joystick',
        'left.joystick.down',
        'left.joystick.up',
        'left.joystick.left',
        'left.joystick.right',
        'right.joystick.down',
        'right.joystick.up',
        'right.joystick.left',
        'right.joystick.right',
    ] as const,
} as const;

// Helper to extract values (0, 1, 2...)
type NumericValues<T> = {
    [K in keyof T]: T[K] extends number ? T[K] : never
}[keyof T];

// PUBLIC TYPES
export type ButtonInput = keyof typeof mappings.aliases | NumericValues<typeof mappings>; 
export type AxisInput = typeof mappings.axisAliases[number];
export type GamepadInput = ButtonInput | AxisInput;

// INTERNAL TYPES
export type InternalAction = number | AxisInput;

export default mappings;
